import React, { useState, useEffect, useRef } from 'react';
import { Clock, MessageSquare, Image, Type, Play, Trash2 } from 'lucide-react';
import { useStore } from '../../stores';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@shared/services/api/ApiService';
import { MessageDialog } from '../../components/common/MessageDialog';
import { getModelDisplayName } from '../../utils/modelDisplay';

interface HistoryTopic {
    topicId: string;
    topicType: 'image' | 'text';
    title: string;
    category: string;
    modelType: string;
    status: string;
    messageCount: number;
    sessionDuration: number;
    rating?: number;
    feedback?: string;
    createdAt: string;
    completedAt?: string;
    lastActivity?: string;
    imageInfo?: {
        imageId: string;
        filename: string;
        fileUrl: string;
    };
    initialQuestion?: string;
}

interface PaginationInfo {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
}

/**
 * 历史记录页面 - 显示用户的学习历史
 */
const HistoryPage: React.FC = () => {
    const [topics, setTopics] = useState<HistoryTopic[]>([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState<'all' | 'image' | 'text'>('all');
    const [selectedTopic, setSelectedTopic] = useState<HistoryTopic | null>(null);
    const [activating, setActivating] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationInfo>({
        page: 1,
        limit: 20,
        total: 0,
        hasMore: false
    });
    const [currentPage, setCurrentPage] = useState(1);
    
    // 消息弹窗状态
    const [messageDialog, setMessageDialog] = useState<{
        isOpen: boolean;
        title?: string;
        message: string;
        type: 'success' | 'error' | 'info';
    }>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info'
    });

    const { user } = useStore();
    const navigate = useNavigate();

    // 显示消息弹窗的辅助函数
    const showMessage = (message: string, type: 'success' | 'error' | 'info' = 'info', title?: string) => {
        setMessageDialog({
            isOpen: true,
            title,
            message,
            type
        });
    };

    const closeMessageDialog = () => {
        setMessageDialog(prev => ({ ...prev, isOpen: false }));
    };

    // 防重复请求的引用
    const lastRequestRef = useRef<string>('');
    const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const loadHistory = async (page: number = currentPage) => {
        // 生成请求标识符
        const requestKey = `${user?.id}-${filter}-${page}`;

        // 防止重复请求
        if (loading || lastRequestRef.current === requestKey) {
            return;
        }

        // 清除之前的超时
        if (requestTimeoutRef.current) {
            clearTimeout(requestTimeoutRef.current);
        }

        lastRequestRef.current = requestKey;
        setLoading(true);

        try {
            // 设置用户token
            if (user?.token) {
                apiService.setToken(user.token);
            }

            const result = await apiService.get(`/history/list?type=${filter}&page=${page}&limit=20`);

            if (result.success) {
                setTopics(result.data?.topics || []);
                setPagination(result.data?.pagination || {
                    page: 1,
                    limit: 20,
                    total: 0,
                    hasMore: false
                });
            }
        } catch (error: any) {
        } finally {
            setLoading(false);
            // 重置请求标识符，允许后续请求
            requestTimeoutRef.current = setTimeout(() => {
                lastRequestRef.current = '';
            }, 1000);
        }
    };

    const handleTopicSelect = (topic: HistoryTopic) => {
        setSelectedTopic(topic);
    };

    const handleLoadTopic = async (topicId: string) => {
        if (activating) return;

        setActivating(true);
        try {
            // 跳转到聊天界面并传递topicId，让ChatInterface加载历史会话
            navigate('/chat', {
                state: {
                    loadHistoryTopicId: topicId,
                    fromHistory: true
                }
            });
        } catch (error) {
            showMessage('载入失败，请稍后重试', 'error');
        } finally {
            setActivating(false);
        }
    };

    const handleDeleteTopic = async (topicId: string) => {
        if (deleting) return;

        setDeleting(topicId);
        try {
            // 设置用户token
            if (user?.token) {
                apiService.setToken(user.token);
            }

            const result = await apiService.delete(`/history/topics/${topicId}`);

            if (result.success) {
                // 从列表中移除已删除的话题
                setTopics(prev => prev.filter(topic => topic.topicId !== topicId));
                setSelectedTopic(null);
                setShowDeleteConfirm(null);

                // 显示成功提示
                showMessage('会话已删除', 'success');
            } else {
                // 处理不同类型的错误响应
                const errorMessage = (result as any).message || result.error?.message || result.error || '删除失败';
                showMessage(errorMessage, 'error');
            }
        } catch (error: any) {
            console.error('删除话题时发生错误:', error);
            const errorMessage = error?.response?.data?.message || error?.message || '删除失败，请稍后重试';
            showMessage(errorMessage, 'error');
        } finally {
            setDeleting(null);
        }
    };

    const confirmDelete = (topicId: string) => {
        setShowDeleteConfirm(topicId);
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(null);
    };

    const handleClosePreview = () => {
        setSelectedTopic(null);
    };

    const getCategoryName = (category: string) => {
        const names: Record<string, string> = {
            'physics': '物理学',
            'chemistry': '化学',
            'math': '数学',
            'others': '其他'
        };
        return names[category] || '未知';
    };

    const getStatusName = (status: string) => {
        const names: Record<string, string> = {
            'completed': '已完成',
            'active': '进行中',
            'disconnected': '已断开',
            'pending': '待开始'
        };
        return names[status] || status;
    };

    const getStatusColor = (status: string) => {
        const colors: Record<string, string> = {
            'completed': 'text-green-600 bg-green-50',
            'active': 'text-blue-600 bg-blue-50',
            'disconnected': 'text-red-600 bg-red-50',
            'pending': 'text-yellow-600 bg-yellow-50'
        };
        return colors[status] || 'text-gray-600 bg-gray-50';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('zh-CN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (seconds: number) => {
        if (seconds < 60) return `${seconds}秒`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟`;
        return `${Math.floor(seconds / 3600)}小时${Math.floor((seconds % 3600) / 60)}分钟`;
    };

    // 处理页面变化
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        loadHistory(newPage);
    };

    // 处理筛选变化
    const handleFilterChange = (newFilter: 'all' | 'image' | 'text') => {
        setFilter(newFilter);
        setCurrentPage(1);
        // loadHistory 会在 useEffect 中被调用
    };

    // 初始加载
    useEffect(() => {
        if (user?.id) {
            loadHistory(currentPage);
        }
    }, [filter, user?.id, currentPage]);

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* 筛选器 */}
            <div className="mb-6">
                <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg w-fit">
                    {[
                        { key: 'all', label: '全部' },
                        { key: 'image', label: '图片问题' },
                        { key: 'text', label: '文本问题' }
                    ].map((item) => (
                        <button
                            key={item.key}
                            onClick={() => handleFilterChange(item.key as 'all' | 'image' | 'text')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === item.key
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 左侧：话题列表 */}
                <div className="lg:col-span-2">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center space-x-4 mb-2">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    历史记录
                                </h2>
                                {/* 简洁的分页控件 */}
                                {pagination.total > pagination.limit && (
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handlePageChange(pagination.page - 1)}
                                            disabled={pagination.page <= 1}
                                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="上一页"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(pagination.page + 1)}
                                            disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="下一页"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                            {/* 分页信息 - 第二行 */}
                            {pagination.total > 0 && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                    显示 {((pagination.page - 1) * pagination.limit + 1)} 到 {Math.min(pagination.page * pagination.limit, pagination.total)} 条，共 {pagination.total} 条记录
                                </div>
                            )}
                        </div>

                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <div className="p-8 text-center">
                                    <p className="text-gray-600 dark:text-gray-400">加载中...</p>
                                </div>
                            ) : topics.length === 0 ? (
                                <div className="p-8 text-center">
                                    <p className="text-gray-600 dark:text-gray-400">暂无历史记录</p>
                                </div>
                            ) : (
                                topics.map((topic) => (
                                    <React.Fragment key={topic.topicId}>
                                        <div
                                            className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${selectedTopic?.topicId === topic.topicId
                                                ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                                                : ''
                                                }`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1" onClick={() => handleTopicSelect(topic)}>
                                                    {/* 话题标题和类型 */}
                                                    <div className="flex items-center space-x-3 mb-2">
                                                        <div className="flex-shrink-0">
                                                            {topic.topicType === 'image' ? (
                                                                <Image className="w-5 h-5 text-blue-500" />
                                                            ) : (
                                                                <Type className="w-5 h-5 text-green-500" />
                                                            )}
                                                        </div>
                                                        <h3 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                                                            {topic.title}
                                                        </h3>
                                                    </div>

                                                    {/* 元数据 */}
                                                    <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400 mb-2">
                                                        <span className="flex items-center space-x-1">
                                                            <Clock className="w-3 h-3" />
                                                            <span>{formatDate(topic.createdAt)}</span>
                                                        </span>
                                                        <span className="flex items-center space-x-1">
                                                            <MessageSquare className="w-3 h-3" />
                                                            <span>{topic.messageCount} 条消息</span>
                                                        </span>
                                                        <span>{getCategoryName(topic.category)}</span>
                                                    </div>

                                                    {/* 状态和模型 */}
                                                    <div className="flex items-center space-x-2">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(topic.status)}`}>
                                                            {getStatusName(topic.status)}
                                                        </span>
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                            {topic.modelType === 'unknown' ? '❓ 未知' : getModelDisplayName(topic.modelType)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* 操作按钮 */}
                                                <div className="flex items-center space-x-2 ml-4">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleLoadTopic(topic.topicId);
                                                        }}
                                                        disabled={activating}
                                                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                        style={{ color: '#07c160' }}
                                                        title="加载会话"
                                                    >
                                                        <Play className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            confirmDelete(topic.topicId);
                                                        }}
                                                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title="删除会话"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 移动端话题详情 - 显示在选中话题下方 */}
                                        {selectedTopic?.topicId === topic.topicId && (
                                            <div className="lg:hidden bg-gray-50 dark:bg-gray-800 border-l-4 border-blue-500 p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                        话题详情
                                                    </h4>
                                                    <button
                                                        onClick={handleClosePreview}
                                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-lg"
                                                    >
                                                        ×
                                                    </button>
                                                </div>

                                                <div className="space-y-3 text-xs">
                                                    {/* 第一行：类型和AI模型 */}
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <span className="text-gray-500 dark:text-gray-400">类型</span>
                                                            <p className="text-gray-900 dark:text-white font-medium">
                                                                {selectedTopic.topicType === 'image' ? '📸 图片问题' : '📝 文本问题'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500 dark:text-gray-400">AI模型</span>
                                                            <p className="text-gray-900 dark:text-white font-medium">
                                                                {selectedTopic.modelType === 'unknown' ? '❓ 未知模型' : getModelDisplayName(selectedTopic.modelType)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 第二行：消息数量和会话时长 */}
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <span className="text-gray-500 dark:text-gray-400">消息数量</span>
                                                            <p className="text-gray-900 dark:text-white font-medium">{selectedTopic.messageCount} 条</p>
                                                        </div>
                                                        {selectedTopic.sessionDuration > 0 && (
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">会话时长</span>
                                                                <p className="text-gray-900 dark:text-white font-medium">{formatDuration(selectedTopic.sessionDuration)}</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* 状态 */}
                                                    <div>
                                                        <span className="text-gray-500 dark:text-gray-400">状态</span>
                                                        <div className="mt-1">
                                                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTopic.status)}`}>
                                                                {getStatusName(selectedTopic.status)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* 时间信息 */}
                                                    <div className="grid grid-cols-1 gap-2">
                                                        <div>
                                                            <span className="text-gray-500 dark:text-gray-400">创建时间</span>
                                                            <p className="text-gray-900 dark:text-white">{formatDate(selectedTopic.createdAt)}</p>
                                                        </div>
                                                        {selectedTopic.lastActivity && (
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">最后活动</span>
                                                                <p className="text-gray-900 dark:text-white">{formatDate(selectedTopic.lastActivity)}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* 继续对话按钮 */}
                                                <button
                                                    onClick={() => handleLoadTopic(selectedTopic.topicId)}
                                                    disabled={activating}
                                                    className="w-full text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 mt-4 disabled:opacity-50"
                                                    style={{ backgroundColor: '#07c160' }}
                                                    onMouseEnter={(e) => !activating && (e.currentTarget.style.backgroundColor = '#06ad56')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                                                >
                                                    <Play className="w-4 h-4" />
                                                    <span>{activating ? '加载中...' : '继续对话'}</span>
                                                </button>
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </div>


                    </div>

                </div>

                {/* 右侧：话题详情 - 只在桌面端显示 */}
                <div className="hidden lg:block lg:col-span-1">
                    {selectedTopic ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    话题详情
                                </h3>
                                <button
                                    onClick={handleClosePreview}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">标题</label>
                                    <p className="text-gray-900 dark:text-white">{selectedTopic.title}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">类型</label>
                                    <p className="text-gray-900 dark:text-white">
                                        {selectedTopic.topicType === 'image' ? '📸 图片问题' : '📝 文本问题'}
                                    </p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">AI模型</label>
                                    <p className="text-gray-900 dark:text-white">
                                        {selectedTopic.modelType === 'unknown' ? '❓ 未知模型' : getModelDisplayName(selectedTopic.modelType)}
                                    </p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">状态</label>
                                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTopic.status)}`}>
                                        {getStatusName(selectedTopic.status)}
                                    </span>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">消息数量</label>
                                    <p className="text-gray-900 dark:text-white">{selectedTopic.messageCount} 条</p>
                                </div>

                                {selectedTopic.sessionDuration > 0 && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">会话时长</label>
                                        <p className="text-gray-900 dark:text-white">{formatDuration(selectedTopic.sessionDuration)}</p>
                                    </div>
                                )}

                                <div>
                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">创建时间</label>
                                    <p className="text-gray-900 dark:text-white">{formatDate(selectedTopic.createdAt)}</p>
                                </div>

                                {selectedTopic.lastActivity && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">最后活动</label>
                                        <p className="text-gray-900 dark:text-white">{formatDate(selectedTopic.lastActivity)}</p>
                                    </div>
                                )}
                            </div>

                            {/* 加载按钮 */}
                            <button
                                onClick={() => handleLoadTopic(selectedTopic.topicId)}
                                disabled={activating}
                                className="w-full text-white font-medium py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2 mt-6 disabled:opacity-50"
                                style={{ backgroundColor: '#07c160' }}
                                onMouseEnter={(e) => !activating && (e.currentTarget.style.backgroundColor = '#06ad56')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                            >
                                <Play className="w-4 h-4" />
                                <span>{activating ? '加载中...' : '继续对话'}</span>
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <p className="text-gray-500 dark:text-gray-400 text-center">
                                选择一个话题查看详情
                            </p>
                        </div>
                    )}
                </div>
            </div>



            {/* 删除确认对话框 */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            确认删除
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            确定要删除这个会话吗？此操作无法撤销。
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={cancelDelete}
                                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                            >
                                取消
                            </button>
                            <button
                                onClick={() => handleDeleteTopic(showDeleteConfirm)}
                                disabled={deleting === showDeleteConfirm}
                                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors"
                            >
                                {deleting === showDeleteConfirm ? '删除中...' : '删除'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 消息弹窗 */}
            <MessageDialog
                isOpen={messageDialog.isOpen}
                title={messageDialog.title}
                message={messageDialog.message}
                type={messageDialog.type}
                onClose={closeMessageDialog}
            />
        </div>
    );
};

export default HistoryPage;